/**
 * 관세사무소별 거래처 ontology view (관리자 전용 사무소 목록).
 *
 * URL: /?audit=trade-partners
 *
 * 왼쪽 = 거래처 list (정렬: 신고 건수 desc)
 * 오른쪽 = 선택 거래처의 HS 분포 (top 50)
 *
 * 데이터는 trade_partner_history (group_id, party_norm, hs_code, decl_date) 에서
 * 직접 집계. 일반 분류사례 (precedent_cases) 와 별개 layer — 통관 실무
 * history 기반 거래처 prior 검증용.
 */
import React, { useEffect, useState } from "react";
import { ArrowLeft, Search, Database, Building2 } from "lucide-react";
import AuditSideNav from "./AuditSideNav";
import { useOfficeGroups } from "./groups";
import "./theme.css";

export default function TradePartnersPage() {
  const { groups: officeGroups } = useOfficeGroups();
  const [groupId, setGroupId] = useState("1005");
  const [parties, setParties] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [hsRows, setHsRows] = useState([]);
  const [hsLoading, setHsLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    setSelected(null); // group 전환 시 선택 초기화
    fetch(`/api/trade-partners?group_id=${groupId}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        setParties(d.parties || []);
        setTotal(d.total || 0);
        if (d.parties?.length) setSelected(d.parties[0]);
      })
      .catch((e) => setErr(String(e.message || e)))
      .finally(() => setLoading(false));
  }, [groupId]);

  useEffect(() => {
    if (!selected) return;
    setHsLoading(true);
    fetch(
      `/api/trade-partners/${encodeURIComponent(selected.party_norm)}/hs?group_id=${groupId}`,
    )
      .then((r) => r.json())
      .then((d) => setHsRows(d.rows || []))
      .catch(() => setHsRows([]))
      .finally(() => setHsLoading(false));
  }, [selected, groupId]);

  const filtered = q.trim()
    ? parties.filter((p) =>
        p.party_norm.toLowerCase().includes(q.trim().toLowerCase()),
      )
    : parties;

  const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        background: "var(--audit-bg)",
        color: "var(--audit-text)",
      }}
    >
      <AuditSideNav />
      <div
        className="audit-root"
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
        }}
      >
        {/* 상단 바 */}
        <div
          style={{
            padding: "10px 16px",
            borderBottom: "1px solid var(--audit-border)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <button
            onClick={() => {
              window.location.href = "/?audit=hs-hub";
            }}
            title="HS 분류 Hub"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: "transparent",
              border: "1px solid var(--audit-border)",
              borderRadius: 4,
              padding: "3px 8px",
              cursor: "pointer",
              fontSize: 12,
              color: "var(--audit-text-muted)",
              fontFamily: "inherit",
            }}
          >
            <ArrowLeft size={12} /> Hub
          </button>
          <span style={{ color: "var(--audit-text-faint)", fontSize: 12 }}>
            /
          </span>
          <Building2 size={16} style={{ color: "var(--audit-text-muted)" }} />
          <h1 style={{ fontSize: 14, margin: 0, fontWeight: 600 }}>
            관세사 거래처 ontology
          </h1>
          {officeGroups.length > 0 && (
          <select
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            title="관세사 group 선택"
            style={{
              padding: "3px 6px",
              fontSize: 12,
              border: "1px solid var(--audit-border-strong)",
              background: "var(--audit-bg)",
              color: "var(--audit-text)",
              borderRadius: 4,
              fontFamily: "inherit",
              marginLeft: 4,
            }}
          >
            {officeGroups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.id} {g.name} · trust {g.trust}
              </option>
            ))}
          </select>
          )}
          <span
            style={{
              fontSize: 12,
              color: "var(--audit-text-muted)",
              fontFamily: "JetBrains Mono, monospace",
            }}
          >
            {filtered.length} / {total} parties
          </span>
        </div>

        {/* 본문 = 좌 list + 우 detail */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* 좌 — 거래처 list */}
          <div
            style={{
              width: 360,
              borderRight: "1px solid var(--audit-border)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: 10,
                borderBottom: "1px solid var(--audit-border)",
                position: "relative",
              }}
            >
              <Search
                size={12}
                style={{
                  position: "absolute",
                  left: 18,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--audit-text-muted)",
                }}
              />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="거래처 검색 (예: vestas)"
                style={{
                  width: "100%",
                  padding: "6px 8px 6px 26px",
                  fontSize: 12,
                  border: "1px solid var(--audit-border-strong)",
                  background: "var(--audit-bg)",
                  color: "var(--audit-text)",
                  borderRadius: 4,
                  fontFamily: "inherit",
                }}
              />
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {loading && (
                <div
                  style={{
                    padding: 16,
                    fontSize: 12,
                    color: "var(--audit-text-muted)",
                  }}
                >
                  로딩...
                </div>
              )}
              {err && (
                <div style={{ padding: 16, fontSize: 12, color: "#d93025" }}>
                  오류: {err}
                </div>
              )}
              {filtered.map((p) => {
                const active = selected?.party_norm === p.party_norm;
                return (
                  <button
                    key={p.party_norm}
                    onClick={() => setSelected(p)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "8px 12px",
                      background: active
                        ? "var(--audit-accent-bg)"
                        : "transparent",
                      borderLeft: active
                        ? "3px solid var(--audit-accent)"
                        : "3px solid transparent",
                      border: "none",
                      borderBottom: "1px solid var(--audit-border)",
                      color: "var(--audit-text)",
                      cursor: "pointer",
                      fontFamily: "inherit",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: active ? 600 : 400,
                      }}
                    >
                      {p.party_norm}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--audit-text-muted)",
                        fontFamily: "JetBrains Mono, monospace",
                        display: "flex",
                        gap: 8,
                      }}
                    >
                      <span>{p.decl_count.toLocaleString()}건</span>
                      <span>·</span>
                      <span>HS6 {p.hs6_variety}종</span>
                      <span>·</span>
                      <span>~{fmtDate(p.last_decl_date)}</span>
                    </div>
                  </button>
                );
              })}
              {!loading && !filtered.length && (
                <div
                  style={{
                    padding: 16,
                    fontSize: 12,
                    color: "var(--audit-text-muted)",
                  }}
                >
                  매치 없음
                </div>
              )}
            </div>
          </div>

          {/* 우 — 선택 거래처의 HS 분포 */}
          <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
            {!selected && (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--audit-text-muted)",
                  padding: 12,
                }}
              >
                좌측에서 거래처를 선택하세요.
              </div>
            )}
            {selected && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <h2
                    style={{
                      fontSize: 16,
                      margin: 0,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Database
                      size={14}
                      style={{ color: "var(--audit-accent)" }}
                    />
                    {selected.party_norm}
                  </h2>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--audit-text-muted)",
                      marginTop: 4,
                      fontFamily: "JetBrains Mono, monospace",
                    }}
                  >
                    총 {selected.decl_count.toLocaleString()}건 · HS6{" "}
                    {selected.hs6_variety}종 ·{" "}
                    {fmtDate(selected.first_decl_date)} ~{" "}
                    {fmtDate(selected.last_decl_date)}
                  </div>
                </div>

                <h3
                  style={{
                    fontSize: 12,
                    color: "var(--audit-text-muted)",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    margin: "12px 0 6px",
                  }}
                >
                  HS Code 분포 (top 50)
                </h3>
                {hsLoading && (
                  <div
                    style={{ fontSize: 12, color: "var(--audit-text-muted)" }}
                  >
                    로딩...
                  </div>
                )}
                {!hsLoading && (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead>
                      <tr style={{ background: "var(--audit-bg-elevated)" }}>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "6px 8px",
                            borderBottom: "1px solid var(--audit-border)",
                            fontWeight: 600,
                            width: 130,
                          }}
                        >
                          HSK (10)
                        </th>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "6px 8px",
                            borderBottom: "1px solid var(--audit-border)",
                            fontWeight: 600,
                            width: 80,
                          }}
                        >
                          HS6
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: "6px 8px",
                            borderBottom: "1px solid var(--audit-border)",
                            fontWeight: 600,
                            width: 70,
                          }}
                        >
                          건수
                        </th>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "6px 8px",
                            borderBottom: "1px solid var(--audit-border)",
                            fontWeight: 600,
                          }}
                        >
                          마지막 신고
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {hsRows.map((r) => (
                        <tr
                          key={r.hs_code}
                          style={{
                            borderBottom: "1px solid var(--audit-border)",
                          }}
                        >
                          <td
                            style={{
                              padding: "6px 8px",
                              fontFamily: "JetBrains Mono, monospace",
                            }}
                          >
                            {r.hs_code}
                          </td>
                          <td
                            style={{
                              padding: "6px 8px",
                              fontFamily: "JetBrains Mono, monospace",
                              color: "var(--audit-text-muted)",
                            }}
                          >
                            {r.hs_6digit}
                          </td>
                          <td
                            style={{
                              padding: "6px 8px",
                              textAlign: "right",
                              fontFamily: "JetBrains Mono, monospace",
                            }}
                          >
                            {r.cnt.toLocaleString()}
                          </td>
                          <td
                            style={{
                              padding: "6px 8px",
                              fontFamily: "JetBrains Mono, monospace",
                              color: "var(--audit-text-muted)",
                            }}
                          >
                            {fmtDate(r.last_decl_date)}
                          </td>
                        </tr>
                      ))}
                      {!hsRows.length && (
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              padding: 12,
                              color: "var(--audit-text-muted)",
                              fontSize: 12,
                            }}
                          >
                            데이터 없음
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}

                <div
                  style={{
                    marginTop: 24,
                    fontSize: 12,
                    color: "var(--audit-text-faint)",
                    lineHeight: 1.6,
                  }}
                >
                  ※ 이 데이터는 선택한 관세사무소의 통관 history
                  에서 추출된 거래처별 HS 분포입니다. AI 분류 [정확] 모드에서
                  seller_name 을 입력하면 이 prior 가 자동으로 LLM 에 주입되어
                  분류 정확도를 높입니다.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
